import { IArticle } from "@/model/IArticle";
import { httpClient } from "./erpClient";
import { Model } from "@/model/Model";
import {
	IDurchzugParameter,
	IScrewThreadParameter,
	ICountersinkParameter,
	IValidationParameter,
	ICylinderheadBoldHoleParameter,
	IPressJoinParameter,
} from "@/model/ParameterTypes";
import { Surface } from "@/model/Enums";
import { GeometryDto, IFeature } from "@/model/IFeature";
import { IWeldlineDto } from "@/model/IWeldlineDto";
import { IValidationMessage } from "@/model/IValidationMessage";
import i18n from "@/plugins/i18n";
import { Vector3 } from "three";
import { IModifyFeaturesRequestDto } from "@/model/IModifyFeaturesRequestDto";
import { IModifyFeaturesResponseDto } from "@/model/IModifyFeaturesResponseDto";
import { IMeasureResponseDto } from "@/model/IMeasureResponseDto";
import { IMarkAsHoleRequestDto } from "@/model/IMarkAsHoleRequestDto";
import { ISetRollDirectionRequestDto } from "@/model/ISetRollDirectionRequest";
import axios from "axios";

export class BlexonErpApi {
	/**
	 * Loads the article data from the erp
	 */
	public static async getArticleData(artikelId: number): Promise<{
		article: IArticle;
		features: IFeature[];
		validationMessages: IValidationMessage[];
		weldlines: IWeldlineDto[];
	}> {
		const response = await httpClient().get(`Cam/GetArticleData?articleId=${artikelId}`);

		return {
			article: {
				id: response.data.id,
				customerId: response.data.customerId,
				isAssembly: response.data.isAssembly,
				isWeldingAllowed: response.data.isWeldingAllowed,
				customerArticlenumber: response.data.kundenArtikelnummer,
				rawMaterialId: response.data.rohmaterialId,
				description: response.data.bezeichnung,
				isReadOnly: response.data.isReadOnly,
				isLocked: response.data.isLocked,
				defaultEMail: response.data.defaultEMail,
				hasElevatedRights: response.data.hasElevatedRights,
				assemblyPositionToArticleIdMap: response.data.assemblyPositionToArticleIdMap,
			},
			features: this.mapErpFeatures(response.data.features),
			validationMessages: this.mapValidationMessages(response.data.validierungsMeldungen),
			weldlines: response.data.weldlines.map((obj: IWeldlineDto) =>
				this.mapToWeldlineObject(obj)
			),
		};
	}

	/**
	 * Gets the model to be rendered from the erp
	 */
	public static async getModel(
		articleId: number,
		getBendzones: boolean,
		lastGroupId: number,
		isAssembly: boolean
	): Promise<Model> {
		var response = await httpClient().get(
			`cam/getModel?articleId=${articleId}&getBendzones=${getBendzones}&groupId=${lastGroupId}&isAssembly=${isAssembly}`
		);
		var model = JSON.parse(response.data.model) as Model;
		if (!getBendzones && model.bodies.some((b) => b.totalNumberOfFaces !== b.faces.length)) {
			for (const body of model.bodies) {
				do {
					response = await httpClient().get(
						`cam/getModel?articleId=${articleId}&` +
							`getBendzones=${getBendzones}&` +
							`groupId=${lastGroupId}&` +
							`bodyMoniker=${body.bodyMoniker}&` +
							`skipFaces=${body.faces.length}&` +
							`isAssembly=${isAssembly}`
					);
					var subModel = JSON.parse(response.data.model) as Model;
					var subModelBody = subModel.bodies.find(
						(b) => b.bodyMoniker === body.bodyMoniker
					);
					body.faces.push(...subModelBody!.faces);
				} while (body.totalNumberOfFaces != body.faces.length);
			}
		}

		model.lastGroupId = response.data.lastGroupId;
		return model;
	}

	public static async measure(
		articleId: number,
		faceExportIds: { exportId: string; order: number }[],
		edgeExportIds: { exportId: string; order: number }[],
		requestId: number
	): Promise<IMeasureResponseDto> {
		var response = await httpClient().post(`cam/measure`, {
			articleId: articleId,
			faceExportIds: faceExportIds,
			edgeExportIds: edgeExportIds,
			requestId: requestId,
		});
		return response.data;
	}

	/**
	 * Modifies all features specified by the list of tasks in the request. All modifications
	 * are executed at once.
	 * @param modifyFeaturesRequest
	 * @returns All executed tasks with information about new features.
	 */
	public static async modifyFeatures(
		modifyFeaturesRequest: IModifyFeaturesRequestDto
	): Promise<IModifyFeaturesResponseDto> {
		const response = await httpClient().post("Cam/ModifyFeatures", modifyFeaturesRequest);
		if (!response.data.successful) {
			throw new Error(i18n.global.t("common.errors.changeCouldNotBeMade"));
		}
		const newFeatures = this.mapErpFeatures(
			response.data.articleAnalyseResultDto.features
		).concat(
			this.mapErpFaces(
				modifyFeaturesRequest.articleId,
				response.data.articleAnalyseResultDto.faces
			)
		);
		return {
			features: newFeatures,
			stepPath: response.data.exportedStepPath,
			tasks: response.data.tasks,
			messages: this.mapValidationMessages(JSON.parse(response.data.messages)),
		};
	}

	/**
	 * Saves the messages, features and weldlines to the server.
	 * The server will save the current scdoc.
	 * @param articleId
	 * @param validationMessages
	 * @param features
	 * @param weldlines
	 */
	public static async saveScDoc(
		articleId: number,
		validationMessages: IValidationMessage[],
		features: IFeature[],
		weldlines: IWeldlineDto[],
		repeatBendSimulation: boolean
	) {
		await httpClient().post(`Cam/saveScDoc?articleId=${articleId}&repeatBendSimulation=${repeatBendSimulation}`, {
			messages: validationMessages.map((m) => {
				return {
					id: 0,
					messageType: m.messageType,
					seite: 1,
					featureIds: m.featureIds,
					state: m.state,
					schweregrad: m.severity,
				};
			}),
			articleAnalyseResultDto: {
				features: features.map((f) => {
					return {
						featureType: f.featureType,
						featureId: f.featureId,
						moniker: null,
						geometry: f.geometry,
						radius: f.radius,
						innerRadius: f.innerRadius,
						angle: f.angle,
						toolId: f.toolId,
						isConfirmed: f.isConfirmed,
						isOnAnchorFace: f.side === Surface.Front,
						invalidFeatureType: f.invalidFeatureType,
						exportIds: f.exportIds,
					};
				}),
			},
			weldlines: weldlines,
		});
	}

	private static mapErpFaces(articleId: number, faces: any[]): IFeature[] {
		return faces.map((f: any) => {
			const feature: IFeature = {
				articleId: articleId,
				angle: 0,
				side: f.faceType,
				exportIds: [f.exportId],
				featureId: f.featureId,
				featureType: f.faceType + 1, // the enum is one value ahead. Yes, this is a hack. :-(
				innerRadius: 0,
				isConfirmed: true,
				radius: 0,
				toolId: "",
				geometry: {} as GeometryDto,
				invalidFeatureType: 0,
				show: false,
			};
			return feature;
		});
	}

	/**
	 * Loads all possible screw thread tools for the specified material from the erp
	 */
	public static async getGewindeParameter(
		rohmaterialId: number
	): Promise<IScrewThreadParameter[]> {
		const response = await httpClient().get(
			"Cam/GetGewindeParameter?rohmaterialId=" + rohmaterialId
		);
		return response.data.map((p: any) => {
			return {
				bezeichnung: p.bezeichnung,
				toolId: p.werkzeugId,
				side: Surface.Front, // not used
				kernlochDurchmesser: p.kernlochDurchmesser,
			};
		});
	}

	/**
	 * Loads all parameters concerning Senkungen
	 */
	public static async getSenkungParameter(
		rohmaterialId: number
	): Promise<ICountersinkParameter[]> {
		const response = await httpClient().get(
			"Cam/GetSenkungParameter?rohmaterialId=" + rohmaterialId
		);
		return response.data.map((p: any) => {
			return {
				bezeichnung: p.bezeichnung,
				toolId: p.werkzeugId,
				side: p.senkungsseite,
				senkungsdurchmesser: p.senkungsdurchmesser,
				upperToleranceSenkungsdurchmesser: p.upperToleranceSenkungsdurchmesser,
				lowerToleranceSenkungsdurchmesser: p.lowerToleranceSenkungsdurchmesser,
				durchgangsdurchmesser: p.durchgangsdurchmesser,
				upperToleranceDurchgangsdurchmesser: p.upperToleranceDurchgangsdurchmesser,
				lowerToleranceDurchgangsdurchmesser: p.lowerToleranceDurchgangsdurchmesser,
				winkel: p.winkel,
			};
		});
	}

	/**
	 * Loads all parameters concerning Zylinderkopfsenkungen
	 */
	public static async getCylinderheadBoltHoleParameter(
		rohmaterialId: number
	): Promise<ICylinderheadBoldHoleParameter[]> {
		const response = await httpClient().get(
			"Cam/GetZylinderkopfSenkungParameter?rohmaterialId=" + rohmaterialId
		);
		return response.data.map((p: any) => {
			return {
				bezeichnung: p.bezeichnung,
				toolId: p.werkzeugId,
				side: p.senkungsseite,
				durchgangsDurchmesser: p.durchgangsDurchmesser,
				tiefeDerSenkung: p.tiefeDerSenkung,
				zylinderDurchmesser: p.zylinderDurchmesser,
			};
		});
	}

	/**
	 * Loads all parameters concerning PressJoin / Einpressungen
	 */
	public static async getPressJoinParameter(
		rohmaterialId: number
	): Promise<IPressJoinParameter[]> {
		const response = await httpClient().get(
			"Cam/getPressJoinParameter?rohmaterialId=" + rohmaterialId
		);
		return response.data.map((p: any) => {
			return {
				bezeichnung: p.werkzeugName,
				toolId: p.werkzeugId,
				side: Surface.Front,
				category: p.category,
				kernlochDurchmesser: p.kernlochDurchmesser,
			};
		});
	}

	/**
	 * Loads all possible extrusion threads for this material from the erp.
	 */
	public static async getDurchzugParameter(rohmaterialId: number): Promise<IDurchzugParameter[]> {
		const response = await httpClient().get(
			"Cam/GetDurchzugParameter?rohmaterialId=" + rohmaterialId
		);
		return response.data.map((p: any) => {
			return {
				bezeichnung: p.bezeichnung,
				toolId: p.werkzeugId,
				side: p.seite,
				kernlochDurchmesser: p.kernlochDurchmesser,
				durchzugHoehe: p.durchzugHoehe,
				durchzugAussendurchmesser: p.durchzugAussendurchmesser,
			};
		});
	}

	/**
	 * Loads validation parameters
	 */
	public static async getValidationParameter(
		rohmaterialId: number,
		articleId: number
	): Promise<IValidationParameter> {
		const response = await httpClient().get(
			`Cam/GetValidationParameter?rohmaterialId=${rohmaterialId}&articleId=${articleId}`
		);
		let validationParameter = response.data;
		validationParameter.defaultBiegeinnenradius = 0;
		return validationParameter;
	}

	private static messageTranslation: any[] = [];

	private static mapValidationMessages(messages: any[]): IValidationMessage[] {
		return messages.map((m: any) => {
			const messageType = m.messageType ?? m.MessageType;
			let title = m.title ?? m.Title;
			let shortDescription = m.shortDescription ?? m.ShortDescription;
			let helpLink = m.helpLink ?? m.HelpLink;

			if (messageType < 100) {
				const translation = this.messageTranslation.find(
					(m: any) => m.messageType === messageType
				);
				if (translation !== undefined) {
					title = translation.newMessageTitle;
					shortDescription = translation.newMessage;
					helpLink = translation.helplink;
				}
			}
			const message: IValidationMessage = {
				messageType: messageType,
				title: title,
				shortDescription: shortDescription,
				featureIds: m.featureIds ?? m.FeatureIds,
				state: m.state ?? m.State,
				severity: m.schweregrad ?? m.Schweregrad,
				helpLink: helpLink,
			};
			return message;
		});
	}

	private static mapErpFeatures(features: any[]): IFeature[] {
		return features.map((f: any) => {
			let lines = f.geometry?.lines;
			if (lines === undefined) {
				const linesObject = JSON.parse(f.geometry);
				if (linesObject != null) {
					lines = linesObject.lines;
				}
			}

			let bend = f.geometry?.bend;
			if (bend === undefined) {
				const bendObject = JSON.parse(f.geometry);
				if (bendObject != null) {
					bend = bendObject.bend;
				}
			}
			const feature: IFeature = {
				articleId: f.articleId,
				angle: f.angle,
				side: f.isOnAnchorFace ? Surface.Front : Surface.Back,
				exportIds: f.exportIds,
				featureId: f.featureId,
				featureType: f.featureType,
				innerRadius: f.innerRadius,
				isConfirmed: f.isConfirmed,
				radius: f.radius,
				toolId: f.toolId,
				geometry: {
					lines: lines,
					bend: bend,
				},
				invalidFeatureType: f.invalidFeatureType,
				show: false,
			};
			return feature;
		});
	}

	private static mapToWeldlineObject(g: IWeldlineDto): IWeldlineDto {
		return {
			weldlineId: g.weldlineId,
			// only weldlines before 2024 have this
			position: new Vector3(g.position.x, g.position.y, g.position.z),
			// only weldlines before 2024 have this
			direction: new Vector3(g.direction.x, g.direction.y, g.direction.z),
			// only weldlines after 2024 have this
			curve: g.curve ? g.curve.map((t: any) => new Vector3(t.x, t.y, t.z)) : null,
			distance: g.distance,
			length: g.length,
			weldCorners: g.weldCorners
				? g.weldCorners.map((c: any) => {
						return {
							position: c.position,
							cornerPoints: c.cornerPoints.map(
								(cp: any) => new Vector3(cp.x, cp.y, cp.z)
							),
							weldCorner: c.weldCorner,
						};
					})
				: null,
			corners: g.corners,
			hovered: false,
			selected: true,
			clean: g.clean === undefined ? true : g.clean,
			assemblyPositionOfBodyA: g.assemblyPositionOfBodyA,
			assemblyPositionOfBodyB: g.assemblyPositionOfBodyB,
			isPlugged: g.isPlugged,
			numberOfSegments: g.numberOfSegments === 0 ? 1 : g.numberOfSegments,
			lengthOfSegment:
				g.lengthOfSegment === 0 ? Math.round(g.length * 10) / 10 : g.lengthOfSegment,
			version: g.version,
			isMesh: g.isMesh,
			isFiller: g.isFiller,
		};
	}

	public static async getWeldlines(articleId: number): Promise<IWeldlineDto[]> {
		const response = await httpClient().get(`Cam/getWeldlines?articleId=${articleId}`);
		return response.data.weldlineGeometries.map((g: any) => this.mapToWeldlineObject(g));
	}

	/**
	 * Marks the specified hole explicitly as a hole. So it will not be recognized as a screw thread
	 * or extruded thread anymore.
	 * @param markAsHoleRequest
	 */
	public static async markAsHole(markAsHoleRequest: IMarkAsHoleRequestDto): Promise<void> {
		const response = await httpClient().post(`Cam/markAsHole`, markAsHoleRequest);
		if (!response.data.successful) {
			throw new Error(i18n.global.t("common.errors.messageCouldNotBeConfirmed"));
		}
	}

	public static async getRollDirection(articleId: number): Promise<{
		angle: number;
		edgeExportId: string;
		position: THREE.Vector3;
		direction: THREE.Vector3;
		length: number;
		normal: THREE.Vector3;
		adjacentFaceId: string;
	}> {
		const response = await httpClient().get(`Cam/GetRollDirection?articleId=${articleId}`);
		return {
			angle: response.data.angle,
			edgeExportId: response.data.edgeExportId,
			length: response.data.length,
			position: new Vector3(
				response.data.position.x,
				response.data.position.y,
				response.data.position.z
			),
			direction: new Vector3(
				response.data.direction.x,
				response.data.direction.y,
				response.data.direction.z
			),
			normal: new Vector3(
				response.data.normal.x,
				response.data.normal.y,
				response.data.normal.z
			),
			adjacentFaceId: response.data.adjacentFaceId,
		};
	}

	/**
	 * Sets the edge and angle of the roll direction to the model.
	 * @param setRollDirectionRequest
	 */
	public static async setRollDirection(
		setRollDirectionRequest: ISetRollDirectionRequestDto
	): Promise<string> {
		const response = await httpClient().post(`Cam/setRollDirection`, setRollDirectionRequest);
		if (!response.data.successful) {
			throw new Error(i18n.global.t("common.errors.rollDirectionSetFailed"));
		}
		return response.data.errorMessage;
	}

	public static async defineWeldlines(
		articleId: number,
		exportIds: string[],
		weldlineIds: number[],
		skipValidation: boolean
	): Promise<{ newWeldlines: IWeldlineDto[]; errorMessages: string[] }> {
		const response = await httpClient().post(`Cam/defineWeldline`, {
			articleId: articleId,
			skipValidation: skipValidation,
			exportIds: exportIds,
			currentWeldlineIds: weldlineIds,
		});
		// If "successful" is false, the task truly ran into an exception and an error is returned.
		if (!response.data.successful) {
			let errorMessage = i18n.global.t("common.errors.weldlineDefinitionError");
			throw new Error(errorMessage);
		}
		return {
			newWeldlines: response.data.weldlineGeometries.map((geometry: any) =>
				this.mapToWeldlineObject(geometry)
			),
			errorMessages: response.data.errorMessage
				.split(";")
				.filter((m: string) => m.trim() !== ""),
		};
	}

	/**
	 * Calls the backend for a live signal. This is needed, as the connection can timeout
	 * and the close cannot be sent to the server. If this live signal is called
	 * regularly, the close call works.
	 */
	public static async live(token: string, articleId: string): Promise<void> {
		await httpClient().post(`Cam/live?token=${token}&articleId=${articleId}`, null);
	}

	/**
	 * Loads message translations into memory
	 * Those translations are used, to show messages during modification of an article properly
	 * @param articleId
	 */
	public static async loadMessageTranslations(articleId: number): Promise<void> {
		const response = await httpClient().get(
			`Cam/getMessageTranslationsWithToken?articleId=${articleId}`
		);
		this.messageTranslation = response.data;
	}

	/**
	 * Fires a close event to shut down all processes for the viewer
	 * @param articleId
	 */
	public static async close(articleId: number): Promise<void> {
		await httpClient().post(`Cam/close?articleId=${articleId}`, null);
	}

	public static async sendArticleToHelpdeskByToken(token: string, email: string): Promise<any> {
		await axios.post(
			`${
				import.meta.env.VITE_APP_SHOP_BACKEND_URL
			}article/SendArticleToHelpdeskByToken?token=${token}&email=${email}`,
			null
		);
	}
}
